import FlowBuilder from "./FlowBuilder"
import axios from "axios"
import SPRYCampaignClient from "./SPRYCampaignClient"
import { CallTypes, ReturnTypes } from "@sprycore/spry-api-client"
import ParticipantBuilder from "./ParticipantBuilder"

type UploadFileArgs = {
    /** The data to upload (Blob, ArrayBuffer, string, Stream, Buffer, etc) */
    data: any
    /** A callback to receive axios upload progress events */
    onUploadProgress?: (progress: { loaded: number, total: number }) => void
    /** The mime type of the file */
    contentType?: string | null
}

type GetFileUploadUrlForUserArgs = Omit<CallTypes.GetFileUploadUrlForUserArgs, "campaignKey">

export default class SessionClient {
    __campaignClient: SPRYCampaignClient
    __sessionKey: string
    __authed?: boolean

    constructor(campaignClient: SPRYCampaignClient, sessionKey: string) {
        this.__campaignClient = campaignClient
        this.__sessionKey = sessionKey
    }

    /**
     * Creates a builder to submit flow data
     */
    createFlowBuilder() { return new FlowBuilder(this) }

    /**
     * Creates a participant builder to submit participant data
     */
    createParticipantBuilder() { return new ParticipantBuilder(this) }

    get campaignClient() { return this.__campaignClient }
    get sessionKey() { return this.__sessionKey }

    /**
     * Uploads a file
     */
    async uploadFile({ data, onUploadProgress, contentType }: UploadFileArgs) {
        contentType = contentType || data.contentType || "application/octet-stream"
        const getFileUploadUrlParams: GetFileUploadUrlForUserArgs = {
            sessionKey: this.__sessionKey,
            contentType: contentType!
        }
        const { uploadUrl, key } = await this.call<ReturnTypes.UploadKey>("getFileUploadUrlForUser", getFileUploadUrlParams)
        try{
            await axios.put(uploadUrl, data, {
                onUploadProgress,
                headers: {
                    "Content-Type": contentType
                }
            })
        }
        catch (e) {
            console.error(e)
            throw e
        }
        return key
    }

    async login(password: string): Promise<boolean> {
        if (!password) { return false }
        const { authed } = await this.call<{ authed: boolean }>("login", { password })
        this.__authed = authed
        return authed
    }

    async logout() {
        await this.call<void>("logout", {})
        this.__authed = false
    }

    async isLoggedIn() {
        if (typeof this.__authed === "boolean") { return this.__authed }
        const { authed } = await this.call<{ authed: boolean }>("isLoggedIn", {})
        this.__authed = authed
        return authed
    }

    async call<T>(method: string, body: any): Promise<T> {
        if (!body) { body = {} }
        body.sessionKey = this.__sessionKey
        return await this.__campaignClient.call<T>(method, body)
    }
}